import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { Input, Textarea, Select } from '@chakra-ui/react';

/**
 * DebouncedInput component that manages local state for immediate feedback
 * while debouncing the onChange callback to parent.
 * 
 * This component is memoized and manages its own internal state to provide
 * smooth typing experience even in large parent components.
 * 
 * @param {Object} props - Component props
 * @param {string} props.value - Controlled value from parent
 * @param {Function} props.onChange - Callback when value changes (debounced)
 * @param {number} props.debounceMs - Debounce delay in milliseconds (default: 250)
 * @param {string} props.as - Render as 'input', 'textarea', or 'select' (default: 'input')
 * @param {React.ReactNode} props.children - Children (for select options)
 * @param {...any} props.rest - Any other props to pass to the underlying Input component
 */
const DebouncedInput = memo(({ 
  value: externalValue, 
  onChange: externalOnChange, 
  debounceMs = 250,
  as = 'input',
  children,
  ...rest 
}) => {
  // Local state for immediate input feedback
  const [localValue, setLocalValue] = useState(externalValue ?? '');
  const timeoutRef = useRef(null);
  const latestValueRef = useRef(localValue);
  const isTypingRef = useRef(false);
  
  // Keep track of the latest value for the debounced callback
  latestValueRef.current = localValue;

  // Sync local value when external value changes (but not during typing)
  useEffect(() => {
    // Only sync if we're not in the middle of typing
    if (!isTypingRef.current && externalValue !== latestValueRef.current) {
      setLocalValue(externalValue ?? '');
    }
  }, [externalValue]);

  // Stable reference to the onChange handler
  const stableOnChange = useRef(externalOnChange);
  stableOnChange.current = externalOnChange;

  // Handle local change with debounced parent update
  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    
    // Update local state immediately for responsive UI
    setLocalValue(newValue);
    isTypingRef.current = true;
    
    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Debounce the parent state update
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      isTypingRef.current = false;
      
      if (stableOnChange.current) {
        // Create a synthetic event-like object with the current value
        // This avoids issues with React's event pooling
        const syntheticEvent = {
          target: { value: latestValueRef.current },
          currentTarget: { value: latestValueRef.current },
        };
        stableOnChange.current(syntheticEvent);
      }
    }, debounceMs);
  }, [debounceMs]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Determine which component to render
  const Component = as === 'textarea' ? Textarea : as === 'select' ? Select : Input;

  return (
    <Component
      {...rest}
      value={localValue}
      onChange={handleChange}
    >
      {children}
    </Component>
  );
});

DebouncedInput.displayName = 'DebouncedInput';

export default DebouncedInput;
